import {useEffect, type Dispatch, type SetStateAction} from 'react'
import {scaleForItem} from '@/lib/bateria'
import {
  ANTIGUEDAD_ANIOS,
  OPCIONES_ANTIGUEDAD,
  claveDeRef,
  type CandidatoIdentidad,
  type DecisionCola,
  type TarjetaCola,
} from '@/lib/bateria-cola'
import {abrirGesto, alternarCasilla, senalando, type GestoEnCurso} from '@/components/bateria/cola/gesto-en-curso'
import {tarjetaContestable} from '@/lib/cola-estados'
import type {DestinoHoja} from '@/components/bateria/cola/tarjeta-ubicar'

// Vive con las tarjetas y no en `hooks/`: `hooks/` es la capa de datos y no puede depender de UI.

function escribiendoTexto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable
}

/** `T` y `M` arman el gesto igual en marcas y en ficha; devuelve si consumió la tecla. */
function armarGesto(e: KeyboardEvent, setGesto: Dispatch<SetStateAction<GestoEnCurso>>): boolean {
  const tipo = {t: 'tachon', m: 'multiple'}[e.key.toLowerCase()]
  if (tipo === undefined) return false
  e.preventDefault()
  setGesto(abrirGesto(tipo as 'tachon' | 'multiple'))
  return true
}

/** En un tachón que señala, `Enter` cruza a elegir la válida. La doble marcación no cruza: dos marcas
 * vivas son un dato perdido (manual p. 76). */
function cruzarAElegirValida(gesto: GestoEnCurso, setGesto: Dispatch<SetStateAction<GestoEnCurso>>): boolean {
  if (gesto === null || gesto.tipo !== 'tachon' || !gesto.senalando) return false
  setGesto({...gesto, senalando: false})
  return true
}

export type TeclasCola = {
  fase: 'tarjetas' | 'cierre' | 'lote'
  actual: TarjetaCola | undefined
  /** Interlock compartido con los botones de la tarjeta: en `false` las teclas de decidir quedan inertes. */
  puedeDecidir: boolean
  /** Pulsa la espera a la vista; sin toast, que a 15-30 tarjetas por segundo sería ruido. */
  alDecidirSinPapel: () => void
  /** Con la evidencia pintada, `puedeDecidir === false` es la fijación de 400 ms o un tránsito
   * retenido: la tecla se traga en silencio. */
  evidenciaALaVista: boolean
  /** Lo elegido en la tarjeta (`''` = «acá no hay dato», `null` = nada): `Enter` confirma esto,
   * nunca la propuesta del motor. */
  seleccionActual: string | null
  gesto: GestoEnCurso
  destino: DestinoHoja | null
  descarteArmado: string | null
  setGesto: Dispatch<SetStateAction<GestoEnCurso>>
  setBuscando: Dispatch<SetStateAction<boolean>>
  setMapaAbierto: Dispatch<SetStateAction<boolean>>
  limpiarUbicar: () => void
  atras: () => void
  adelante: () => void
  continuar: () => void
  dejarParaDespues: (tarjeta: TarjetaCola) => void
  descartarHoja: (tarjeta: TarjetaCola) => void
  armarODescartar: (tarjeta: TarjetaCola, descartar: (tarjeta: TarjetaCola) => void) => void
  asignarHoja: (tarjeta: TarjetaCola, elegido: DestinoHoja) => void
  cerrarGesto: (tarjeta: TarjetaCola) => void
  tachonSinRespuesta: (tarjeta: TarjetaCola) => void
  registrar: (tarjeta: TarjetaCola, parcial: Partial<DecisionCola>) => void
  /** Va aparte de `registrar` porque el gesto en curso viaja con la respuesta. */
  elegirCampo: (tarjeta: TarjetaCola, valor: string | null) => void
  /** `null` = se entró con `Enter` (queda el valor propuesto); una cadena = el carácter tecleado. */
  comenzarAEscribir: (inicial: string | null) => void
  opcionesDeCampo: (campo: string) => string[]
  /** Pedidos aparte porque leerlos rasteriza el PDF de la hoja; vacíos mientras se leen. */
  candidatos: CandidatoIdentidad[]
  seleccionarCandidato: (tarjeta: TarjetaCola, i: number, candidatos: CandidatoIdentidad[]) => void
  /** `P` gana sobre escribir en S4, como `D`: una tecla no cambia de sentido según la sección. */
  irASiguientePorMarcar: () => boolean
}

/** Escucha en captura para correr antes que el manejador de `TiraOpciones` y poder
 * `preventDefault()` durante un gesto (`T`/`M`). */
export function useTeclasCola({
  fase,
  actual,
  puedeDecidir,
  alDecidirSinPapel,
  evidenciaALaVista,
  seleccionActual,
  gesto,
  destino,
  descarteArmado,
  setGesto,
  setBuscando,
  setMapaAbierto,
  limpiarUbicar,
  atras,
  adelante,
  continuar,
  dejarParaDespues,
  descartarHoja,
  armarODescartar,
  asignarHoja,
  cerrarGesto,
  tachonSinRespuesta,
  registrar,
  elegirCampo,
  comenzarAEscribir,
  opcionesDeCampo,
  candidatos,
  seleccionarCandidato,
  irASiguientePorMarcar,
}: TeclasCola) {
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return
      if (escribiendoTexto(e.target)) return
      if (e.key === '?') {
        e.preventDefault()
        setMapaAbierto((abierto) => !abierto)
        return
      }
      if (e.key === 'Escape') {
        setGesto(null)
        limpiarUbicar()
        setMapaAbierto(false)
        return
      }
      if (e.key.toLowerCase() === 'p') {
        e.preventDefault()
        irASiguientePorMarcar()
        return
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        if (e.key === 'ArrowLeft') atras()
        else adelante()
        return
      }
      if (fase !== 'tarjetas') {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (fase === 'cierre') continuar()
        }
        return
      }
      if (!actual) return
      if (e.key.toLowerCase() === 'd') {
        e.preventDefault()
        dejarParaDespues(actual)
        return
      }
      // Misma regla que los botones; va después de las flechas y `D` porque navegar no es decidir.
      if (!tarjetaContestable(actual)) return
      // Sin evidencia no se decide ni se encola: una tecla sostenida caería en ráfaga sobre la
      // tarjeta equivocada.
      if (!puedeDecidir) {
        if (e.key === 'Enter' || e.key.length === 1) {
          e.preventDefault()
          if (!evidenciaALaVista) alDecidirSinPapel()
        }
        return
      }
      const digito = Number(e.key)
      const esDigito = Number.isInteger(digito) && e.key.trim() !== ''

      if (actual.tipo === 'ubicar') {
        if (esDigito && digito >= 1 && digito <= candidatos.length) {
          e.preventDefault()
          seleccionarCandidato(actual, digito - 1, candidatos)
        }
        if (e.key.toLowerCase() === 'b') {
          e.preventDefault()
          setBuscando((abierto) => !abierto)
        }
        if (e.key.toLowerCase() === 'x') {
          e.preventDefault()
          armarODescartar(actual, descartarHoja)
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          if (descarteArmado === actual.ref) descartarHoja(actual)
          else if (destino) asignarHoja(actual, destino)
        }
        return
      }

      if (actual.tipo === 'marca') {
        const escala = scaleForItem(claveDeRef(actual.ref) ?? '')
        if (e.key.toLowerCase() === 'x' && actual.flag_marca === 'tachon') {
          e.preventDefault()
          tachonSinRespuesta(actual)
          return
        }
        if (armarGesto(e, setGesto)) return
        if (gesto && senalando(gesto) && esDigito && digito >= 1 && digito <= escala.length) {
          // Primera mitad: el dígito nombra una casilla tachada o marcada, no elige.
          e.preventDefault()
          setGesto(alternarCasilla(gesto, escala[digito - 1]))
          return
        }
        if (gesto && esDigito && digito >= 1 && digito <= escala.length) {
          // Segunda mitad de un tachón: el dígito es la respuesta y viaja con el gesto.
          e.preventDefault()
          registrar(actual, {valor: escala[digito - 1], gesto: gesto.tipo, casillas: gesto.casillas})
          return
        }
        if (e.key === 'Enter') {
          // `Enter` no acepta la sospecha en marcas (§1.5): el lector no pudo ver qué casilla es.
          e.preventDefault()
          if (!cruzarAElegirValida(gesto, setGesto)) cerrarGesto(actual)
        }
        return
      }

      const campo = claveDeRef(actual.ref) ?? ''
      // La ficha usa el vocabulario de las marcas; sin gesto en curso, `Enter` acepta la propuesta (§1.5).
      if (actual.tipo === 'confirmar' || actual.tipo === 'antiguedad') {
        const esAntiguedad = actual.tipo === 'antiguedad'
        const opciones = esAntiguedad ? OPCIONES_ANTIGUEDAD : opcionesDeCampo(campo)
        if (e.key === '0') {
          e.preventDefault()
          elegirCampo(actual, null)
          return
        }
        if (armarGesto(e, setGesto)) return
        if (esDigito && digito >= 1 && digito <= opciones.length) {
          e.preventDefault()
          const opcion = opciones[digito - 1]
          // Primera mitad del gesto: el dígito nombra una casilla, igual que en las marcas.
          if (senalando(gesto)) setGesto(alternarCasilla(gesto!, opcion))
          // «Un año o más» no es respuesta: entra a escribir el número.
          else if (esAntiguedad && opcion === ANTIGUEDAD_ANIOS) comenzarAEscribir(null)
          else elegirCampo(actual, opcion)
          return
        }
        if (e.key === 'Enter') {
          e.preventDefault()
          if (cruzarAElegirValida(gesto, setGesto)) return
          if (gesto) cerrarGesto(actual)
          // El chip «acá no hay dato» se elige con `''` y el POST espera `null`.
          else if (seleccionActual !== null) elegirCampo(actual, seleccionActual === '' ? null : seleccionActual)
          else if (actual.valor_propuesto !== null) elegirCampo(actual, actual.valor_propuesto)
        }
        return
      }

      // S4: a la tarjeta se llega con el foco suelto; solo `Enter` o un carácter entran al campo.
      if (e.key === 'Enter') {
        e.preventDefault()
        comenzarAEscribir(null)
        return
      }
      if (e.key.length === 1) {
        e.preventDefault()
        comenzarAEscribir(e.key)
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [
    fase,
    actual,
    puedeDecidir,
    alDecidirSinPapel,
    evidenciaALaVista,
    seleccionActual,
    gesto,
    destino,
    descarteArmado,
    setGesto,
    setBuscando,
    setMapaAbierto,
    limpiarUbicar,
    atras,
    adelante,
    continuar,
    dejarParaDespues,
    descartarHoja,
    armarODescartar,
    asignarHoja,
    cerrarGesto,
    tachonSinRespuesta,
    registrar,
    elegirCampo,
    comenzarAEscribir,
    opcionesDeCampo,
    candidatos,
    seleccionarCandidato,
    irASiguientePorMarcar,
  ])
}
